/**
 * SSE-text fallback parser for the non-streaming chat path. `postChat` uses this
 * when the agent gateway returns raw SSE text despite `stream:false` (e.g. on
 * native CapacitorHttp, which can't consume the streaming body).
 */
import { synthesizeToolSummary } from '@/lib/chat-response-synthesis'
import type { ChatUIAction, ChatCompletionResponse } from './chat.types'

/** Parse raw SSE text into a ChatCompletionResponse. */
export function parseSSEText(sseText: string, onProgress?: (status: string) => void): ChatCompletionResponse {
  let text = ''
  const toolCalls: Array<{ name: string; params: any }> = []
  const toolResults: Array<Record<string, unknown>> = []
  let routing: Record<string, unknown> | string | undefined
  let stats: Record<string, unknown> | undefined
  let uiActions: ChatUIAction[] | undefined

  // Parse SSE events: "event: <type>\ndata: <json>\n\n"
  const eventRegex = /event:\s*(\w+)\ndata:\s*(.+?)(?=\n\nevent:|\n\n$|\n*$)/gs
  let match: RegExpExecArray | null
  while ((match = eventRegex.exec(sseText)) !== null) {
    const eventType = match[1]
    let data: any
    try {
      data = JSON.parse(match[2])
    } catch {
      continue
    }

    switch (eventType) {
      case 'text':
        if (data?.text) text += data.text
        break
      case 'start':
        if (data?.routing) routing = data.routing
        if (data?.message && onProgress) onProgress(data.message)
        break
      case 'status':
        if (data?.message && onProgress) onProgress(data.message)
        break
      case 'tool_start':
        if (data?.toolCalls?.length) {
          toolCalls.push(...data.toolCalls)
          if (onProgress) {
            const names = data.toolCalls.map((tc: any) => tc.name).filter(Boolean)
            if (names.length > 0) {
              const friendly = names[0].replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
              onProgress(`Running: ${friendly}...`)
            }
          }
        }
        break
      case 'tool_result':
      case 'tool_receipt':
      case 'tool_error':
        toolResults.push({ type: eventType, ...data })
        if (onProgress && data?.summary) {
          onProgress(data.ok ? `Done: ${data.summary}` : `Error: ${data.summary}`)
        }
        break
      case 'ui-action':
        // Mid-stream UI actions (report.init, report.section, report.status, module.focus, etc.)
        // On web these are handled in real-time via SSE streaming. On Capacitor (non-streaming),
        // we collect them here so they can be processed after the response completes.
        if (data?.type) {
          if (!uiActions) uiActions = []
          uiActions.push(data as ChatUIAction)
        }
        break
      case 'complete':
        if (data?.stats) {
          stats = data.stats
        }
        if (data?.routing) routing = data.routing
        // Merge any uiActions from the complete event (some are sent here too)
        if (Array.isArray(data?.uiActions)) {
          if (!uiActions) uiActions = []
          uiActions.push(...data.uiActions)
        }
        break
      case 'error':
        return { text: '', error: { message: data?.message || data?.error || 'Chat error' } }
    }
  }

  // If the SSE stream carried no `text` frames but we did see successful
  // tool work (e.g. a directive-only edit that applied html.patch blocks
  // or a create_module/update_module call on the agent side), synthesize
  // a terse user-facing summary from the tool diff instead of persisting
  // the literal "No response" bubble the old fallback produced.
  //
  // If we can't synthesize either (no chunks, no tools, no directives)
  // return empty `text` and let the caller decide how to surface the
  // outcome. useChat.ts's non-streaming fallback path is the only caller
  // and treats empty text + no tool work as a transient-error case
  // (layer 3 — a retry bubble, not a persisted literal).
  const synthesised =
    text.trim() ? '' : synthesizeToolSummary(toolCalls, toolResults, uiActions)

  return {
    text: text || synthesised || '',
    toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
    toolResults: toolResults.length > 0 ? toolResults : undefined,
    stats,
    routing,
    uiActions,
  }
}
